/* Shared bits of the trasseapp.com site: page addresses, links, page titles. */
import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/** Design file name → address on the site. */
export const SITE_ROUTES = {
  SiteHome: '/',
  SiteHow: '/how-it-works',
  SiteSchools: '/schools',
  SiteParents: '/parents',
  SiteDrivers: '/drivers',
  SiteZana: '/zana',
  SitePricing: '/pricing',
  SiteFamilies: '/trasse-families',
  SiteFAQ: '/questions',
  SiteBook: '/book',
  SiteThanks: '/thanks',
  SiteSignIn: '/sign-in',
  SiteAbout: '/about',
  SiteSafety: '/safety',
  SiteContact: '/contact',
  // The live, reviewed legal pages stay as they are (static files in /public).
  SitePrivacy: '/privacy',
  SiteTerms: '/terms',
  Site404: '/404',
} as const;
export type SitePage = keyof typeof SITE_ROUTES;

/** Pages served outside the SPA router (full page load). */
const STATIC: Partial<Record<SitePage, true>> = { SitePrivacy: true, SiteTerms: true };

export function pageOf(pathname: string): SitePage | null {
  const p = pathname.replace(/\/+$/, '') || '/';
  const hit = (Object.keys(SITE_ROUTES) as SitePage[]).find((k) => SITE_ROUTES[k] === p);
  return hit || null;
}

/** Spread onto an <a>: a real href plus client-side navigation. */
export function useSiteGo() {
  const navigate = useNavigate();
  return useCallback((page: SitePage | string, hash = '') => {
    const known = (SITE_ROUTES as Record<string, string>)[page];
    const href = (known || page) + hash;
    if (!known || STATIC[page as SitePage]) return { href };
    return {
      href,
      onClick: (e: React.MouseEvent) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(href);
      },
    };
  }, [navigate]);
}

export function usePageTitle(title: string, description?: string) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const prevDesc = meta?.content;
    if (description) {
      if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
      meta.content = description;
    }
    return () => { document.title = prev; if (meta && prevDesc !== undefined) meta.content = prevDesc; };
  }, [title, description]);
}

/** Parse a design-style inline style string ("width: 40%; ...") into an object. */
export function css(s?: string | null): React.CSSProperties {
  const out: Record<string, string> = {};
  (s || '').split(';').forEach((d) => {
    const i = d.indexOf(':');
    if (i < 0) return;
    const k = d.slice(0, i).trim();
    if (!k) return;
    out[k.startsWith('--') ? k : k.replace(/-([a-z])/g, (_, c) => c.toUpperCase())] = d.slice(i + 1).trim();
  });
  return out as React.CSSProperties;
}

/** The one Trasse app parents and drivers install. */
export const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.pear.mobile';
export const APP_STORE_URL = 'https://apps.apple.com/ke/app/trasse/id6787725982';
